import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, ChevronLeft, ChevronRight, KeyRound, Mail, ShieldCheck, Volume2 } from 'lucide-react'
import { GoogleLogin } from '@react-oauth/google'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import { useAuth } from '@/context/AuthContext'
import { apiClient } from '@/api/client'
import { getMcpConnection, revokeMcpConnection } from '@/api/mcp'
import { requestPushPermission, unsubscribeFromPush } from '@/lib/push'
import { isEffectSoundEnabled, setEffectSoundEnabled } from '@/lib/effectSettings'

/** プッシュ通知に必要な API がブラウザで使えるか。 */
function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

export default function SettingsPage() {
  const { user, linkGoogle, unlinkGoogle, logout } = useAuth()
  const navigate = useNavigate()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [unlinkOpen, setUnlinkOpen] = useState(false)
  const [mcpRevokeOpen, setMcpRevokeOpen] = useState(false)
  const queryClient = useQueryClient()
  const { data: mcpConnection } = useQuery({
    queryKey: ['mcpConnection'],
    queryFn: getMcpConnection,
  })
  const revokeMcp = useMutation({
    mutationFn: revokeMcpConnection,
    onSuccess: () => toast.success('Claude との連携を解除しました'),
    onError: () => toast.error('連携の解除に失敗しました'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['mcpConnection'] }),
  })
  // 機能判定・許可状態は同期で確定するので初期値に織り込む（effect 内同期 setState を回避）
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(() =>
    !pushSupported() || Notification.permission === 'denied' ? false : null,
  )
  const [pushDenied, setPushDenied] = useState(() => pushSupported() && Notification.permission === 'denied')
  const [pushLoading, setPushLoading] = useState(false)
  const [effectSound, setEffectSound] = useState(isEffectSoundEnabled)

  const handleEffectSoundToggle = (checked: boolean) => {
    setEffectSoundEnabled(checked)
    setEffectSound(checked)
  }

  useEffect(() => {
    if (!pushSupported() || Notification.permission === 'denied') return
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushEnabled(sub !== null))
      .catch(() => setPushEnabled(false))
  }, [])

  const handlePushToggle = async (checked: boolean) => {
    setPushLoading(true)
    try {
      if (checked) {
        const ok = await requestPushPermission()
        if (!ok && Notification.permission === 'denied') {
          setPushDenied(true)
          toast.error('ブラウザの設定から通知を許可してください')
        }
        setPushEnabled(ok)
      } else {
        await unsubscribeFromPush()
        setPushEnabled(false)
        toast.success('プッシュ通知をオフにしました')
      }
    } finally {
      setPushLoading(false)
    }
  }

  const handleLinkGoogle = async (credential: string) => {
    try {
      await linkGoogle(credential)
      toast.success('Google アカウントを連携しました')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'Google 連携に失敗しました')
    }
  }

  const handleUnlinkGoogle = async () => {
    try {
      await unlinkGoogle()
      toast.success('Google 連携を解除しました')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'Google 連携の解除に失敗しました')
    }
  }

  // MCP コネクタ URL。本番は同一オリジン配下の /api/mcp。env で上書き可能。
  const mcpUrl = (import.meta.env.VITE_MCP_URL as string | undefined) ?? `${window.location.origin}/api/mcp`

  const handleCopyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl)
      toast.success('コネクタ URL をコピーしました')
    } catch {
      toast.error('コピーに失敗しました')
    }
  }

  const handleDeleteAccount = async () => {
    try {
      await apiClient.delete('/auth/account/')
      logout()
      navigate('/login', { replace: true })
      toast.success('アカウントを削除しました')
    } catch {
      toast.error('アカウントの削除に失敗しました')
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-[env(safe-area-inset-top)] z-10 flex h-14 items-center bg-background border-b px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mr-2 flex items-center justify-center min-h-[44px] min-w-[44px] -ml-2"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-base font-semibold">設定</h1>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* アカウント情報 */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">アカウント</h2>
          <div className="rounded-lg border bg-card divide-y">
            <div className="flex items-center gap-3 px-4 py-3">
              <Mail size={16} className="text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">メールアドレス</p>
                <p className="text-sm font-medium truncate">{user?.email ?? '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <ShieldCheck size={16} className="text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">ログイン方法</p>
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {user?.has_password && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full">メール/パスワード</span>
                  )}
                  {user?.is_google_linked && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full">Google</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Google 連携 */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">Google 連携</h2>
          {!user?.is_google_linked ? (
            <div className="rounded-lg border bg-card px-4 py-4 space-y-2">
              <p className="text-sm text-muted-foreground">Google アカウントと連携すると、どちらでもログインできるようになります。</p>
              <div className="flex justify-center scale-90 origin-center">
                <GoogleLogin
                  onSuccess={(res) => { if (res.credential) handleLinkGoogle(res.credential) }}
                  onError={() => toast.error('Google 連携に失敗しました')}
                  text="continue_with"
                  width={280}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-lg border bg-card divide-y">
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="text-sm flex-1">Google 連携済み</span>
              </div>
              {user.has_password ? (
                <button
                  type="button"
                  className="w-full flex items-center px-4 py-3 min-h-[44px] text-sm text-destructive gap-2"
                  onClick={() => setUnlinkOpen(true)}
                >
                  連携を解除
                </button>
              ) : (
                <div className="px-4 py-3 space-y-1">
                  <button
                    type="button"
                    disabled
                    className="w-full flex items-center text-sm text-muted-foreground gap-2 cursor-not-allowed"
                  >
                    連携を解除
                  </button>
                  <p className="text-xs text-muted-foreground">
                    パスワードを設定してから解除できます
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Claude / MCP 連携 */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">Claude と連携</h2>
          {mcpConnection?.connected && (
            <div className="rounded-lg border bg-card divide-y">
              <div className="px-4 py-3 space-y-1">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" aria-hidden />
                  連携中（{mcpConnection.clients.join('・')}）
                </p>
                {mcpConnection.last_authorized_at && (
                  <p className="text-xs text-muted-foreground">
                    最終認証{' '}
                    {new Date(mcpConnection.last_authorized_at).toLocaleString('ja-JP', {
                      month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  連携から登録した実績 {mcpConnection.achievement_count}件
                </p>
              </div>
              <button
                type="button"
                className="w-full flex items-center px-4 py-3 min-h-[44px] text-sm text-destructive gap-2 disabled:opacity-50"
                disabled={revokeMcp.isPending}
                onClick={() => setMcpRevokeOpen(true)}
              >
                連携を解除
              </button>
            </div>
          )}
          <div className="rounded-lg border bg-card px-4 py-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Claude などの MCP 対応クライアントに下記 URL をカスタムコネクタとして追加すると、
              チャットから実績を登録できます（初回は Claude 側でログイン・許可が必要です）。
            </p>
            {!mcpConnection?.connected && (mcpConnection?.achievement_count ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground">
                これまでに連携から登録した実績 {mcpConnection?.achievement_count ?? 0}件
              </p>
            )}
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded bg-muted px-2 py-1.5 text-xs">{mcpUrl}</code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="min-h-[44px] shrink-0"
                onClick={handleCopyMcpUrl}
              >
                コピー
              </Button>
            </div>
          </div>
        </section>

        {/* 通知 */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">通知</h2>
          <div className="rounded-lg border bg-card divide-y">
            <div className="flex items-center gap-3 px-4 py-3 min-h-[44px]">
              <Bell size={16} className="text-muted-foreground shrink-0" />
              <span className="text-sm font-medium flex-1">プッシュ通知</span>
              <Switch
                checked={pushEnabled ?? false}
                onCheckedChange={handlePushToggle}
                disabled={pushLoading || pushEnabled === null || pushDenied}
              />
            </div>
            {pushDenied && (
              <p className="px-4 py-2 text-xs text-muted-foreground">
                ブラウザの設定から通知を許可してください
              </p>
            )}
          </div>
        </section>

        {/* 演出 */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">演出</h2>
          <div className="rounded-lg border bg-card divide-y">
            <div className="flex items-center gap-3 px-4 py-3 min-h-[44px]">
              <Volume2 size={16} className="text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">効果音・振動</p>
                <p className="text-xs text-muted-foreground">AI判定の結果演出で鳴らす（この端末のみ）</p>
              </div>
              <Switch checked={effectSound} onCheckedChange={handleEffectSoundToggle} />
            </div>
          </div>
        </section>

        {/* セキュリティ */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">セキュリティ</h2>
          <div className="rounded-lg border bg-card divide-y">
            {user?.has_password ? (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px]"
                onClick={() => navigate('/settings/change-password')}
              >
                <KeyRound size={16} className="text-muted-foreground shrink-0" />
                <span className="text-sm font-medium flex-1 text-left">パスワードを変更</span>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>
            ) : (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px]"
                onClick={() => navigate('/settings/set-password')}
              >
                <KeyRound size={16} className="text-muted-foreground shrink-0" />
                <span className="text-sm font-medium flex-1 text-left">パスワードを設定</span>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>
            )}
          </div>
        </section>

        {/* ログアウト */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">その他</h2>
          <div className="rounded-lg border bg-card divide-y">
            <button
              type="button"
              className="w-full text-left px-4 py-3 text-sm font-medium min-h-[44px]"
              onClick={() => { logout(); navigate('/login', { replace: true }) }}
            >
              ログアウト
            </button>
          </div>
        </section>

        {/* アカウント削除 */}
        <section>
          <Button
            variant="ghost"
            className="w-full bg-destructive/10 text-destructive hover:bg-destructive/20"
            onClick={() => setDeleteOpen(true)}
          >
            アカウントを削除
          </Button>
        </section>
      </div>

      <ConfirmDialog
        open={unlinkOpen}
        onOpenChange={setUnlinkOpen}
        title="Google 連携を解除しますか？"
        description="解除後は Google でログインできなくなります。"
        confirmLabel="解除する"
        variant="destructive"
        onConfirm={handleUnlinkGoogle}
      />

      <ConfirmDialog
        open={mcpRevokeOpen}
        onOpenChange={setMcpRevokeOpen}
        title="Claude との連携を解除しますか？"
        description="解除すると Claude から実績を登録できなくなります。登録済みの実績は残ります。再度使うと Claude 側でログインを求められます。"
        confirmLabel="解除する"
        variant="destructive"
        onConfirm={() => revokeMcp.mutate()}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="アカウントを削除しますか？"
        description="すべてのデータが削除されます。この操作は元に戻せません。"
        confirmLabel="削除する"
        variant="destructive"
        onConfirm={handleDeleteAccount}
      />
    </div>
  )
}
